<template>
    <!--
        Modal presentation (per-org "terms_consent_presentation" = modal):
        read-only scroll box + "View Full Terms of Consent" modal + an explicit
        "I accept these Terms of Consent" checkbox + single "Ok" acknowledgment button.
        The button is disabled until the checkbox is checked. Enforcement/persistence are
        unchanged; this only changes how the consent is presented.
    -->
    <div v-if="modal" class="flex flex-col gap-3 w-full">
        <p v-if="reconsentNotice" class="body-small text-ink-muted text-left">{{ reconsentNotice }}</p>
        <BaseCard theme="muted" padding="sm" class="max-h-60 overflow-y-auto text-left">
            <MarkdownText class="details text-ink-muted" :text="termsContent" />
        </BaseCard>
        <BaseButton theme="tertiary" size="sm" class="self-start" @click="isFullTermsOpen = true"> {{ t("components.termsConsent.viewFull") }} </BaseButton>
        <BaseCheckbox v-model="termsModel" shape="square" class="body-small text-control-label justify-center">{{ t("components.termsConsent.accept") }}</BaseCheckbox>
        <BaseButton theme="primary" class="w-full" :disabled="!termsModel" @click="acknowledge">{{ t("components.termsConsent.ok") }}</BaseButton>

        <BaseDialog v-model:open="isFullTermsOpen" :title="t('components.termsConsent.termsOfUse')">
            <MarkdownText class="details text-ink-muted" :text="termsContent" />
        </BaseDialog>
    </div>

    <!--
        Default presentation: inline scrollable terms followed by an explicit
        "I accept these Terms of Consent" checkbox. Checking it records consent
        (`terms` becomes true), which is what parent pages gate their submit button on.
    -->
    <div v-else class="flex flex-col gap-3 w-full">
        <p v-if="reconsentNotice" class="body-small text-ink-muted text-left">{{ reconsentNotice }}</p>
        <BaseCard theme="muted" padding="sm" class="max-h-80 overflow-y-auto text-left">
            <MarkdownText class="text-balance *:details *:text-ink-muted" :text="termsContent"></MarkdownText>
        </BaseCard>
        <BaseCheckbox v-model="termsModel" shape="square" class="body-small text-control-label justify-center">{{ t("components.termsConsent.accept") }}</BaseCheckbox>
    </div>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    termsContent: { type: String, default: null },
    // When true, render the scroll-box + modal + "Ok" button presentation instead of the
    // default inline terms. Driven by the org's terms_consent_presentation config.
    modal: { type: Boolean, default: false },
    // Why the user is being (re-)prompted (server-computed). A non-first-time reason renders
    // a one-line notice above the terms; full-page interstitials say it in their own heading
    // instead and must not pass this prop, to avoid double messaging.
    reconsentReason: { type: String, default: "first_time" },
});

const reconsentNotice = computed(() => {
    switch (props.reconsentReason) {
        case "updated":
            return t("components.termsConsent.updated");
        case "grandfathered":
            return t("components.termsConsent.grandfathered");
        default:
            return null;
    }
});

const emit = defineEmits(["accept"]);

const termsModel = defineModel("terms", { type: Boolean });

const isFullTermsOpen = ref(false);

function acknowledge() {
    termsModel.value = true;
    emit("accept");
}
</script>

<style scoped></style>
